import React, { FC, useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import fixWebmDuration from 'webm-duration-fix';
import {
  ConfirmationProps,
  PREVIOUS_VERSION_MESSAGE,
  PREVIOUS_VERSION_TITLE,
  REVIEW_LOCKED_MESSAGE,
  REVIEW_LOCKED_TITLE,
} from './constants';
import { ConfirmationDialog } from '../../../../components/Modal/ConfirmationDialog';
import { FeedbackHeader } from './FeedbackHeader';
import { useVideoFeedback } from './useVideoFeedback';
import { FeedbackVideoPanel } from './FeedbackVideoPanel';
import { CreateCommentPanel } from './CreateCommentPanel';
import { CommentsListPanel } from './CommentsListPanel';
import { ApplyCommentDialog } from './ApplyCommentDialog';
import { IconChatMsgs } from '../../../../utils/icons';
import { formatWebOptimized } from '../../../admin/videos/selectedVideoCardV2/helpers';

interface Props {
  confirmationData?: ConfirmationProps;
  onConfirmationClose: () => void;
}

export const VideoFeedback: FC<Props> = ({
  confirmationData,
  onConfirmationClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    project,
    video,
    getComments,
    disableCommentMessage,
    isReviewLocked,
    onCopyComment,
    commentToApply,
    setCommentToApply,
    readOnly,
  } = useVideoFeedback();
  // Feedback already submitted → the project is in Editing. Keep the player,
  // drop the comment box, and say why (the header carries the same notice).
  const lockedMessage = readOnly ? PREVIOUS_VERSION_MESSAGE : REVIEW_LOCKED_MESSAGE;
  const lockedTitle = readOnly ? PREVIOUS_VERSION_TITLE : REVIEW_LOCKED_TITLE;
  const commentBlockedMessage = isReviewLocked ? lockedMessage : disableCommentMessage;
  // Video Feedback TODO
  // const [video, setVideo] = useState(project?.projectFiles?.[0]);
  const repairedBlobRef = useRef<Blob | null>(null); 


  const repairWebm = async (url: string) => {
    fetch(url)
    .then(response => response.blob())  // Convert the response to a Blob
    .then(async(blob) => {
        repairedBlobRef.current = await fixWebmDuration(blob); 
        const newUrl = URL.createObjectURL(repairedBlobRef.current);
        if(!videoRef.current) return;
        videoRef.current.src = newUrl;
        videoRef.current.load(); // Load the new video source
    })
    .catch(error => {
        console.log('Error fetching the video:', error);
    });
  };

  useEffect(() => {
    getComments(video?.id);

    
    if (!videoRef.current) return;
    if (video) {
      const webOptimized = formatWebOptimized(video);
      // Only reload when the source actually changed — parents may pass a
      // fresh video object identity for the same file.
      if (videoRef.current.src !== webOptimized.url) {
        videoRef.current.src = webOptimized.url; // Use the optimized version for better performance
        videoRef.current.load(); // Load the new video source
      }
      // repairWebm(formatWebM(video).url); // we need to actually fix the webm in the pipeline
    }

  }, [video, project?.id]);


  return (
    <Box className="flex justify-center">
      <Box className="flex flex-col w-full px-6 items-center justify-between gap-6 max-w-[1480px]">
        <FeedbackHeader />
        <Box className="flex gap-4 w-full">
          <Box className="flex flex-col gap-4 w-[65%]">
            <FeedbackVideoPanel
              videoRef={videoRef}
            />
            {!commentBlockedMessage && (
              <CreateCommentPanel videoRef={videoRef} />
            )}
            {commentBlockedMessage && (
              <Box className="h-full w-full flex flex-col items-center justify-center gap-3 border border-border-token rounded-2xl px-6 py-8 text-center">
                <Box
                  className="flex items-center justify-center rounded-full shrink-0"
                  sx={{ width: 76, height: 76, backgroundColor: 'var(--tint)' }}
                >
                  <IconChatMsgs size={34} />
                </Box>
                {isReviewLocked && (
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                    {lockedTitle}
                  </Typography>
                )}
                <Typography
                  className="text-text-2"
                  sx={{ fontSize: 12.5, lineHeight: 1.6, maxWidth: 340 }}
                >
                  {commentBlockedMessage}
                </Typography>
              </Box>
            )}
          </Box>
          <Box className="flex flex-col gap-4 w-[35%]">
            <CommentsListPanel video={video || {}} />
          </Box>
        </Box>

        {confirmationData && (
          <ConfirmationDialog
            show={Boolean(confirmationData)}
            {...confirmationData}
            onCancel={onConfirmationClose}
            onConfirm={async () => {
              await confirmationData?.onConfirm();
              onConfirmationClose();
            }}
          />
        )}

        {/* One picker for every comment's "Apply to other videos" item. */}
        {commentToApply && onCopyComment && (
          <ApplyCommentDialog
            comment={commentToApply}
            onClose={() => setCommentToApply(null)}
          />
        )}
      </Box>
    </Box>
  );
};
